import React, { useEffect } from "react";
import { Accordion, Flex, Text, TextArea } from "@theorgchartdev/storybook";
import { CardWrapper } from "../../Cards/CardWrapper/CardWrapper";
import { appstr } from "@coreapp/utils/appstr";
import useResizable from "@/hooks/useResizable";
import { RecordEditorFooter } from "./Components/RecordEditorFooter";
import { GetWSMgr } from "@/Utils/StateLib";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { getContainerHeight, getContainerWidth } from "@/Utils/getContainerSize";

export const BaseRecordEditorModal = ({
  show,
  overrideObj,
  dbObj,
  type,
  onSave,
  onClose,
  manager,
  dataTestIdPrefix = "base-record-editor-",
  loadRecord = true,
  otherHeaderProps = {},
  otherFooterProps = {},
  overlayProps = {},
  size: propSize,
  initOffset: propInitOffset,
  showFooterRevert = false,
  children,
  headerContent,
  getHeaderTitle,
  onSaveWithChanges,
  showComments = true,
  // Controller data passed from parent
  controllerData = {},
  // Controller functions passed from parent
  onUnsavedChanges,
  onRevertOverride,
  onInitializeTarget,
  onLoadRecordFallback,
  contentBefore,
  includeTopPadding = true,
  includeScrollbar = true,
  requireDbObj = true,
  isMultiEdit = false,
  action = null,
  allowSave = true
}) => {
  // Set up Resizable Window
  const initialWidth = propSize?.width || 700;
  const initialHeight = propSize?.height || 650;
  const minWidth = 450;
  const minHeight = 400;

  const { size, handleMouseDown } = useResizable(initialWidth, initialHeight, minWidth, minHeight);
  const initOffset = propInitOffset || {
    x: (getContainerWidth() - initialWidth) / 2,
    y: (getContainerHeight() - initialHeight) / 2
  };

  // Extract controller data from props
  const {
    record,
    baseRecord,
    recordSupervisor,
    override,
    existingSupervisorID,
    changeResults,
    changeType,
    madeFieldReverts,
    CommentOnlyChange,
    workingOverride,
    comment,
    UpdateComment
  } = controllerData;

  const UnsavedChanges = () => {
    return onUnsavedChanges ? onUnsavedChanges() : false;
  };

  const changeTarget = callbackFn => {
    // Check if we need to save
    const unsavedChanges = UnsavedChanges();
    if (unsavedChanges !== true) {
      // Proceed to change target
      if (onInitializeTarget) {
        onInitializeTarget(overrideObj, dbObj, loadRecord);
      }
      return;
    }
    // Throw save alert to save or discard changes before target change
    throwSaveAlert(callbackFn);
  };

  const throwSaveAlert = callbackFn => {
    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstr("Save Changes", "?"),
      appstr("Save your current changes to this record", "?"),
      async () => {
        let saveResult;

        // If we have a custom save function with changes, use it
        if (onSaveWithChanges) {
          saveResult = await onSaveWithChanges(true); // validate=true
        } else {
          // Otherwise use the standard save function
          saveResult = await handleSaveRecordChanges(true); // validate=true
        }

        // Only proceed if save was successful
        if (saveResult && saveResult.success !== false) {
          callbackFn?.();
        }
      },
      () => {
        callbackFn?.();
      },
      appstr("Save Changes"),
      appstr("Discard")
    );
  };

  useEffect(() => {
    // change target when override or dbobj prop change
    changeTarget(() => {
      if (manager) manager.handleRecord?.(overrideObj, dbObj, type);
    });

    // Load a copy of the record, as a fallback.
    if (onLoadRecordFallback) {
      let recordID = overrideObj?.SmartGetFieldValue("RefID");
      onLoadRecordFallback(recordID, dbObj?.IsPositionObj());
    }
  }, [overrideObj, dbObj]);

  const closeHandler = () => {
    const unsavedChanges = UnsavedChanges();
    if (unsavedChanges !== true) {
      onClose();
      return;
    }
    throwSaveAlert(() => onClose());
  };

  if (!manager || (requireDbObj && !dbObj) || !overrideObj) {
    onClose();
    return null;
  }

  if (!workingOverride) return null;

  const GetChangeType = () => {
    return changeType ?? workingOverride.SmartGetFieldValue("Type");
  };

  const RevertEmployeeOverride = () => {
    const wsMgr = GetWSMgr();
    wsMgr.CreateReactAlert(
      appstr("Revert All Changes"),
      appstr("Revert All Confirm"),
      () => {
        DoRevertAll();
      },
      () => {},
      appstr("Revert"),
      appstr("Cancel")
    );
  };

  const RevertPositionOverride = () => {
    const wsMgr = GetWSMgr();
    const { alertProps, message } = createBoldToastMessage("Revert all position changes info", {
      positionRevertWarning: appstr("positionRevertWarning")
    });

    wsMgr.CreateReactAlert(
      appstr("Revert All Changes"),
      <Flex direction={"column"} gap="24px">
        <Text dynamic={{ ...alertProps }}>{message}</Text>
        <Text>{appstr("Revert all position confirm")}</Text>
        <Text modifier={"bold"}>{appstr("This action cannot be undone")}</Text>
      </Flex>,
      () => {
        DoRevertAll();
      },
      () => {},
      appstr("Revert"),
      appstr("Cancel")
    );
  };

  const DoRevertAll = () => {
    if (onRevertOverride) {
      onRevertOverride();
    }
    onClose();
  };

  const handleSaveRecordChanges = async (validate = true, additionalChanges = {}) => {
    // This will be handled by the parent component through onSaveWithChanges
    if (onSaveWithChanges) {
      return await onSaveWithChanges(validate, additionalChanges);
    }
    // Fallback to basic save
    return await onSave({ comment });
  };

  const showRevert = () => {
    if (manager.GetManagerName?.() !== "planManager") return false;
    if (isMultiEdit) return false; // No revert for multi-edit for now
    return workingOverride.fChanges.length > 0 && !CommentOnlyChange();
  };

  // Set up Footer
  const footerProps = {
    spacingClass: "oc-overlay-footer--space-between",
    modifier: "grey",
    footerActions: {
      customFooterContent: (
        <RecordEditorFooter
          showRevert={showFooterRevert || showRevert()}
          onSave={onSaveWithChanges || handleSaveRecordChanges}
          onClose={closeHandler}
          dataTestIdPrefix={dataTestIdPrefix}
          onRevert={dbObj?.IsPositionObj() ? RevertPositionOverride : RevertEmployeeOverride}
          action={action}
          allowSave={allowSave}
        />
      )
    }
  };

  const getHeaderProps = () => {
    return {
      title: getHeaderTitle(),
      modifier: "grey",
      ...otherHeaderProps
    };
  };

  return (
    <CardWrapper
      show={show}
      prefixId={dataTestIdPrefix}
      contentClass={"ocn-chart-explorer-content"}
      onClose={closeHandler}
      focusTrap
      otherHeaderProps={getHeaderProps()}
      otherFooterProps={{ ...footerProps, ...otherFooterProps }}
      size={size}
      onMouseDownResize={handleMouseDown}
      initOffset={initOffset}
      minWidth={minWidth}
      minHeight={minHeight}
      otherOverlayProps={{
        padding: 0,
        showOverlay: true,
        ...overlayProps
      }}
    >
      {contentBefore}
      <Flex
        gap={"20px"}
        direction={"column"}
        height={"100%"}
        style={{ paddingTop: includeTopPadding ? "20px" : "0" }}
      >
        {headerContent}

        <div className="ocn-chart-explorer-column" style={{ flex: 1, overflow: "hidden" }}>
          <div
            className="ocn-list-scrollbar"
            style={
              includeScrollbar
                ? { height: "100%", overflowY: "auto", paddingRight: "8px" }
                : { height: "100%" }
            }
          >
            {children}

            {/* common components for all modals goes here */}
            {showComments && (
              <div style={{ padding: "0 16px 0 16px" }}>
                <Accordion
                  name={
                    <Text size={"medium"} modifier={"bold"}>
                      {appstr("Comments")}
                    </Text>
                  }
                  initExpand={false}
                  iconPosition="right"
                  labelProps={{
                    backgroundColor: "orange",
                    hoverColor: "orange",
                    customStyle: {
                      justifyContent: "space-between",
                      padding: "12px",
                      marginTop: "16px"
                    }
                  }}
                  // Note: Comments are handled by the BaseRecordEditorModal
                  beforeContent={
                    <Flex>
                      <div style={{ width: "100%" }}>
                        <TextArea
                          value={comment ?? ""}
                          onChange={UpdateComment}
                          placeholder={appstr("Enter comment")}
                        />
                      </div>
                    </Flex>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </Flex>
    </CardWrapper>
  );
};

export default BaseRecordEditorModal;
